import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { CARE_PLAN_DIALOG_OPTIONS } from '../care-plan-dialog.options';
import { ACTION_API, ActionApi } from './api/action-api.token';
import {
  ActionModalEntryPoint,
  ActionUpdateEditorSource,
} from './action-editor-source';
import {
  ActionEditorDialogComponent,
  ActionEditorDialogData,
} from './action-editor-dialog/action-editor-dialog.component';
import { buildActionUpdateRequest } from './action-update-request';
import {
  ActionOwner,
  ActionReferenceValue,
  CarePlanAction,
  humanizeActionCode,
} from './actions.model';
import {
  ACTION_OWNER_API,
  ActionOwnerApi,
} from './owners/action-owner-api.token';
import {
  ACTION_REFERENCE_API,
  ActionReferenceApi,
} from './reference/action-reference-api.token';

export interface OpenActionModalOptions {
  entryPoint: ActionModalEntryPoint;
  /**
   * An optional feature-owned editor source. Care Plan supplies its scoped
   * store so its list refresh remains intact. The dialog owns modal analytics. Other callers can
   * omit this and use the app-level record editor.
   */
  editor?: ActionUpdateEditorSource;
}

/**
 * Application-level entry point for viewing or editing one Action by id.
 *
 * Consumers do not need a selected client, mounted Care Plan, or Action list.
 * Inject this service and call {@link open}; the service loads the record and
 * authoritative System Code references before displaying the shared editor.
 */
@Injectable()
export class ActionModalService {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly _dialog = inject(DialogService);
  private readonly _api = inject<ActionApi>(ACTION_API);
  private readonly _references =
    inject<ActionReferenceApi>(ACTION_REFERENCE_API);
  private readonly _owners = inject<ActionOwnerApi>(ACTION_OWNER_API);
  private readonly _messages = inject(MessageService, { optional: true });

  async open(
    actionId: string | number,
    options: OpenActionModalOptions,
  ): Promise<CarePlanAction | null> {
    const id = this._actionId(actionId);
    if (!id) {
      this._showError('A valid Action ID is required.');
      return null;
    }

    try {
      const actionPromise = firstValueFrom(this._api.getAction(id));
      const [action, editor] = await Promise.all([
        actionPromise,
        options.editor
          ? Promise.resolve(options.editor)
          : this._loadStandaloneEditor(),
      ]);
      const ref = this._dialog.open(ActionEditorDialogComponent, {
        ...CARE_PLAN_DIALOG_OPTIONS,
        width: '42rem',
        breakpoints: { '768px': 'calc(100vw - 2rem)' },
        styleClass: 'bn-action-editor-dialog-host',
        data: {
          mode: 'edit',
          entryPoint: options.entryPoint,
          editor,
          action,
        } satisfies ActionEditorDialogData,
      });
      if (!ref) return null;
      const result = await firstValueFrom(ref.onClose, { defaultValue: null });
      return this._isAction(result) ? result : null;
    } catch (error) {
      this._showError(error instanceof Error ? error.message : 'Try again.');
      return null;
    }
  }

  private async _loadStandaloneEditor(): Promise<ActionUpdateEditorSource> {
    const [references, ownerData] = await Promise.all([
      firstValueFrom(this._references.loadReferenceData()),
      firstValueFrom(this._owners.loadOwners()),
    ]);
    if (
      references.actionTypes.length === 0 ||
      references.statuses.length === 0 ||
      references.priorities.length === 0 ||
      references.communicationPreferences.length === 0
    ) {
      throw new Error('Action reference data is unavailable.');
    }
    const owners = new Map(
      ownerData.owners.map((owner) => [owner.id, owner] as const),
    );

    return {
      actionTypes: () => references.actionTypes,
      statuses: () => references.statuses,
      priorities: () => references.priorities,
      communicationPreferences: () => references.communicationPreferences,
      ownerLabel: (ownerId) => this._ownerLabel(owners, ownerId),
      statusLabel: (statusCode) =>
        this._referenceLabel(references.statuses, statusCode),
      update: (id, value, options) =>
        this._actionLogs.run(
          value.assignedToId !== undefined
            ? 'care_plan.action.reassign'
            : 'care_plan.action.update',
          id,
          async (attempt) => {
            attempt.identifiers = { action_id: id };
            attempt.stage = 'save';
            return firstValueFrom(
              attempt.request(
                this._api.updateAction(id, buildActionUpdateRequest(value)),
              ),
            );
          },
          options?.eventOwner !== 'modal',
        ),
    };
  }

  private _referenceLabel(
    values: readonly ActionReferenceValue[],
    code: string | null,
  ): string {
    return (
      values.find((value) => value.code === code)?.label ??
      humanizeActionCode(code)
    );
  }

  private _ownerLabel(
    owners: ReadonlyMap<string, ActionOwner>,
    ownerId: string | null,
  ): string {
    if (!ownerId) return 'Owner unavailable';
    return owners.get(ownerId)?.displayName ?? 'Financial Assistant';
  }

  private _actionId(value: string | number): string | null {
    const normalized = String(value).trim();
    const numeric = Number(normalized);
    return Number.isSafeInteger(numeric) && numeric > 0
      ? String(numeric)
      : null;
  }

  private _isAction(value: unknown): value is CarePlanAction {
    return (
      !!value &&
      typeof value === 'object' &&
      typeof (value as Partial<CarePlanAction>).id === 'string'
    );
  }

  private _showError(detail: string): void {
    this._messages?.add({
      severity: 'error',
      summary: "Couldn't open action",
      detail,
    });
  }
}
